import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import EstimateForm from '../../../components/feature/EstimateForm';
import FinalCTA from '../../home/components/FinalCTA';
import { locations } from '../../../mocks/locations';
import { locationFaqs } from '../../../mocks/faqs';
import { servicesMeta } from '../../../mocks/services';

interface LocationPageTemplateProps {
  locationSlug: string;
}

export default function LocationPageTemplate({ locationSlug }: LocationPageTemplateProps) {
  const [formOpen, setFormOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const loc = locations.find((l) => l.slug === locationSlug);
  if (!loc) return null;
  const faqs = locationFaqs(loc.name);

  const aiAnswers = [
    {
      q: `Who provides hardwood flooring services in ${loc.name}, Maryland?`,
      a: `Premier Hardwood Floors provides professional installation, refinishing, and repair services throughout ${loc.name}, Maryland. Specializing in residential projects, they deliver expert craftsmanship tailored to local homeowners and ${loc.name}'s diverse architectural styles.`,
    },
    {
      q: `What hardwood flooring services are available in ${loc.name}?`,
      a: `In ${loc.name}, Premier Hardwood Floors offers hardwood floor installation, sanding and refinishing, water and structural damage repair, and full floor replacement. Every service includes a free in-home estimate with transparent, written pricing.`,
    },
    {
      q: `What is the best hardwood flooring for ${loc.name} homes?`,
      a: `Given Maryland's seasonal humidity, white oak and red oak are excellent choices for ${loc.name} homes due to dimensional stability and durability. Engineered hardwood is also popular for basement levels. Our specialists recommend the ideal option during your free in-home consultation.`,
    },
  ];

  return (
    <div className="min-h-screen font-sans">
      <Header onEstimateClick={() => setFormOpen(true)} />
      <main>
        {/* Hero */}
        <section className="relative min-h-[560px] flex items-end pb-0 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={loc.image}
              alt={`Hardwood flooring services in ${loc.name}, Maryland`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/70" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/20" />
          </div>
          <div className="relative max-w-6xl mx-auto px-6 w-full pt-36 pb-16">
            <nav className="flex gap-2 text-sm text-white/70 font-sans mb-6">
              <Link to="/" className="hover:text-gold transition-colors">Home</Link>
              <span>/</span>
              <Link to="/locations" className="hover:text-gold transition-colors">Locations</Link>
              <span>/</span>
              <span className="text-gold">{loc.name}</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-map-pin-fill text-gold text-xs" />
              <span className="font-sans text-gold text-xs font-semibold">{loc.name}, Maryland</span>
            </div>
            <h1
              className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-4"
              style={{ textShadow: '0 2px 16px rgba(0,0,0,0.85), 0 1px 4px rgba(0,0,0,0.9)' }}
            >
              Hardwood Flooring Services
              <span className="block text-gold text-3xl md:text-4xl mt-2">in {loc.name}, Maryland</span>
            </h1>
            <p
              className="font-sans text-lg text-white max-w-xl mb-9 leading-relaxed"
              style={{ textShadow: '0 1px 8px rgba(0,0,0,0.8)' }}
            >
              {loc.description}
            </p>

            {/* Local trust signals */}
            <div className="flex flex-wrap gap-4 mb-9">
              {['Licensed & Insured', '5-Star Rated', 'Free Estimates', 'Local Experts'].map((item) => (
                <div key={item} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5">
                  <i className="ri-check-line text-gold text-xs" />
                  <span className="text-white font-sans text-xs font-medium">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setFormOpen(true)}
                className="px-8 py-3.5 bg-gold text-walnut font-bold rounded-xl hover:bg-gold-dark transition-colors font-sans text-sm whitespace-nowrap"
              >
                Get a Free Estimate
              </button>
              <a
                href="tel:+14105550192"
                className="px-8 py-3.5 border-2 border-white/30 text-white rounded-xl font-sans text-sm font-semibold hover:bg-white/10 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-phone-fill" /> (410) 555-0192
              </a>
            </div>
          </div>
        </section>

        {/* Services in Location */}
        <section className="bg-white py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="font-sans text-xs uppercase tracking-widest text-oak font-semibold">Available in {loc.name}</span>
              <h2 className="font-serif text-3xl md:text-4xl text-walnut font-bold mt-3">
                Hardwood Flooring Services in {loc.name}
              </h2>
              <p className="font-sans text-charcoal/55 text-sm mt-4 max-w-lg mx-auto">
                Every service, fully available to {loc.name} homeowners — with the same precision and quality as anywhere in Maryland.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {servicesMeta.map((svc) => (
                <Link
                  key={svc.id}
                  to={`/services/${svc.slug}`}
                  className="flex gap-5 bg-sand rounded-2xl p-7 border border-sand-dark hover:border-oak/30 transition-all cursor-pointer group hover:-translate-y-1"
                >
                  <div className="w-13 h-12 flex items-center justify-center bg-walnut/10 rounded-xl flex-shrink-0 group-hover:bg-walnut transition-colors" style={{ width: '3rem' }}>
                    <i className={`${svc.icon} text-oak text-xl group-hover:text-gold transition-colors`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-sans text-base font-bold text-walnut mb-1.5 group-hover:text-oak transition-colors">
                      {svc.name} in {loc.name}
                    </h3>
                    <p className="font-sans text-sm text-charcoal/55 leading-relaxed line-clamp-2">{svc.shortDesc}</p>
                    <div className="flex items-center gap-1.5 text-oak font-sans text-xs font-semibold mt-3 group-hover:gap-2.5 transition-all">
                      Learn More <i className="ri-arrow-right-line" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Mid-page CTA */}
            <div className="mt-10 bg-walnut rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
              <div>
                <h3 className="font-serif text-xl text-white font-bold">Serving {loc.name} Homeowners</h3>
                <p className="text-white/60 font-sans text-sm mt-1">Free in-home estimate — limited availability, book now.</p>
              </div>
              <button
                onClick={() => setFormOpen(true)}
                className="px-8 py-3.5 bg-gold text-walnut font-bold rounded-xl hover:bg-gold-dark transition-colors font-sans text-sm whitespace-nowrap flex-shrink-0"
              >
                Request Free Estimate
              </button>
            </div>
          </div>
        </section>

        {/* Map Section — Prominent */}
        <section className="bg-sand py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="font-sans text-xs uppercase tracking-widest text-oak font-semibold">Service Coverage</span>
              <h2 className="font-serif text-3xl text-walnut font-bold mt-3">
                Serving {loc.name} &amp; Surrounding Areas
              </h2>
              <p className="font-sans text-charcoal/55 text-sm mt-4">
                We cover all of {loc.name} and neighboring communities throughout Maryland.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden border-2 border-sand-dark mb-8" style={{ height: '500px' }}>
              <iframe
                title={`Premier Hardwood Floors service area in ${loc.name}, MD`}
                src={loc.mapEmbed}
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              />
            </div>
            {/* Neighborhood highlights */}
            <div className="bg-white rounded-2xl p-8 border border-sand-dark">
              <h3 className="font-sans text-sm font-bold text-walnut mb-5 uppercase tracking-wide">
                <i className="ri-map-pin-2-line text-gold mr-2" />
                {loc.name} Neighborhoods We Serve
              </h3>
              <div className="flex flex-wrap gap-2.5">
                {loc.highlights.map((h) => (
                  <span key={h} className="px-4 py-2 bg-sand border border-sand-dark rounded-full text-sm font-sans text-walnut font-medium">
                    {h}
                  </span>
                ))}
                <span className="px-4 py-2 bg-gold/10 border border-gold/20 rounded-full text-sm font-sans text-oak font-medium">
                  + All of {loc.name}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* AI Answers */}
        <section className="bg-walnut py-24 px-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]"
            style={{ backgroundImage: 'repeating-linear-gradient(-45deg, #C8A96A 0, #C8A96A 1px, transparent 0, transparent 40%)', backgroundSize: '18px 18px' }}
          />
          <div className="relative max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-robot-line text-gold text-sm" />
                <span className="font-sans text-xs text-white/70 font-semibold uppercase tracking-wide">AI &amp; Voice Search Optimized</span>
              </div>
              <h2 className="font-serif text-3xl text-white font-bold mt-1">
                Hardwood Flooring in {loc.name} — Quick Answers
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {aiAnswers.map((item) => (
                <div key={item.q} className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/10 transition-colors">
                  <div className="w-9 h-9 flex items-center justify-center bg-gold/20 rounded-xl mb-4">
                    <i className="ri-question-line text-gold text-base" />
                  </div>
                  <h3 className="font-sans text-sm font-bold text-white mb-3 leading-snug">{item.q}</h3>
                  <p className="font-sans text-sm text-white/65 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local Trust */}
        <section className="bg-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-3xl text-walnut font-bold mb-5">
              Trusted by {loc.name} Homeowners
            </h2>
            <p className="font-sans text-base text-charcoal/65 leading-relaxed mb-10 max-w-2xl mx-auto">
              Serving homeowners throughout {loc.name} with reliable, high-quality hardwood flooring solutions tailored to local homes, architectural styles, and Maryland&apos;s climate. Every project is backed by our licensed team, transparent pricing, and satisfaction guarantee.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { icon: 'ri-shield-check-line', label: 'Licensed & Insured', sub: 'Fully certified in Maryland' },
                { icon: 'ri-map-pin-2-line', label: 'Local Knowledge', sub: `${loc.name} specialists` },
                { icon: 'ri-star-fill', label: '5-Star Rated', sub: '500+ verified reviews' },
              ].map((item) => (
                <div key={item.label} className="bg-sand rounded-2xl p-7 border border-sand-dark text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-gold/15 rounded-2xl mx-auto mb-4">
                    <i className={`${item.icon} text-gold text-2xl`} />
                  </div>
                  <div className="font-sans text-base font-bold text-walnut mb-1">{item.label}</div>
                  <div className="font-sans text-xs text-charcoal/50">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-sand py-24 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-white border border-sand-dark rounded-full px-4 py-1.5 mb-5">
                <i className="ri-question-answer-line text-oak text-sm" />
                <span className="font-sans text-xs text-charcoal/55 font-semibold uppercase tracking-wide">Voice Search Optimized</span>
              </div>
              <h2 className="font-serif text-3xl text-walnut font-bold mt-1">
                {loc.name} Hardwood Flooring FAQ
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className={`border rounded-2xl overflow-hidden ${openFaq === i ? 'border-oak/40 bg-white' : 'border-sand-dark bg-white'}`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer hover:bg-sand/50 transition-colors"
                  >
                    <span className={`font-sans text-base font-bold pr-4 leading-snug ${openFaq === i ? 'text-oak' : 'text-walnut'}`}>{faq.q}</span>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors ${openFaq === i ? 'bg-oak text-white' : 'bg-sand text-oak border border-sand-dark'}`}>
                      <i className={`text-sm ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`} />
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-7 pb-7">
                      <div className="h-px bg-sand-dark mb-5" />
                      <p className="font-sans text-sm text-charcoal/65 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Schema FAQ */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            })}} />
          </div>
        </section>

        <FinalCTA
          onEstimateClick={() => setFormOpen(true)}
          heading={`Get Hardwood Flooring in ${loc.name} Today`}
          subtext={`Premier Hardwood Floors proudly serves ${loc.name} and surrounding Maryland communities. Free estimate — limited availability.`}
        />
      </main>
      <Footer onEstimateClick={() => setFormOpen(true)} />
      <EstimateForm isOpen={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}
